import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { client } from '../../amplify-client';
import { GuennieRating } from '../../components/GuennieRating';
import { formatDatum } from '../../lib/format';
import { uebersetzeSchreibFehler } from '../../lib/fehler';
import type { Schema } from '../../../amplify/data/resource';

type Test = Schema['SpaghettiEisTest']['type'];
type EisdieleKurz = { id: string; name: string; ort: string };

const STATUS_LABEL: Record<string, string> = { ENTWURF: 'Entwurf', VEROEFFENTLICHT: 'Veröffentlicht' };

/**
 * Verwaltung → Verkostungen (Bauphase 7): ALLE Verkostungen, jeder Status,
 * jeder Ersteller — nicht nur die eigenen (siehe "Meine"). Nur hier
 * erreichbar, weil `client.models.SpaghettiEisTest.list()` als Verwalter
 * dank `allow.group('Verwalter')` (ohne Owner-Einschränkung) wirklich alles
 * liefert.
 */
export function VerwaltungVerkostungen() {
  const [tests, setTests] = useState<Test[]>([]);
  const [eisdielen, setEisdielen] = useState<Record<string, EisdieleKurz>>({});
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [aktionId, setAktionId] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [testerFilter, setTesterFilter] = useState('');

  const laden = useCallback(() => {
    setLaedt(true);
    setFehler(null);
    Promise.all([
      client.models.SpaghettiEisTest.list({ limit: 1000 }),
      client.models.Eisdiele.list({ selectionSet: ['id', 'name', 'ort'], limit: 2000 }),
    ])
      .then(([testRes, eisdieleRes]) => {
        if (testRes.errors?.length) throw new Error(testRes.errors[0].message);
        setTests([...(testRes.data ?? [])].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')));
        const map: Record<string, EisdieleKurz> = {};
        for (const e of eisdieleRes.data ?? []) map[e.id] = { id: e.id, name: e.name, ort: e.ort };
        setEisdielen(map);
      })
      .catch((e) => setFehler(String(e?.message ?? e)))
      .finally(() => setLaedt(false));
  }, []);

  useEffect(() => laden(), [laden]);

  const testerNamen = useMemo(
    () => Array.from(new Set(tests.map((t) => t.testerName))).sort((a, b) => a.localeCompare(b, 'de')),
    [tests],
  );

  const gefiltert = useMemo(
    () =>
      tests.filter((t) => {
        if (statusFilter && t.status !== statusFilter) return false;
        if (testerFilter && t.testerName !== testerFilter) return false;
        return true;
      }),
    [tests, statusFilter, testerFilter],
  );

  async function zuruecknehmen(t: Test) {
    setAktionId(t.id);
    setFehler(null);
    try {
      const { errors } = await client.mutations.eisTestZuruecknehmen({ id: t.id });
      if (errors?.length) throw new Error(errors[0].message);
      laden();
    } catch (e) {
      setFehler(uebersetzeSchreibFehler(e instanceof Error ? e.message : String(e)));
    } finally {
      setAktionId(null);
    }
  }

  async function loeschen(t: Test) {
    const eisdiele = eisdielen[t.eisdieleId];
    if (!confirm(`Verkostung „${eisdiele?.name ?? 'Verkostung'}" von ${t.testerName} wirklich unwiderruflich löschen?`)) return;
    setAktionId(t.id);
    setFehler(null);
    try {
      const { errors } = await client.mutations.eisTestLoeschen({ id: t.id });
      if (errors?.length) throw new Error(errors[0].message);
      laden();
    } catch (e) {
      setFehler(uebersetzeSchreibFehler(e instanceof Error ? e.message : String(e)));
    } finally {
      setAktionId(null);
    }
  }

  return (
    <div>
      <div className="karte" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
        <div>
          <label htmlFor="f-status">Status</label>
          <select id="f-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">alle</option>
            <option value="ENTWURF">Entwurf</option>
            <option value="VEROEFFENTLICHT">Veröffentlicht</option>
          </select>
        </div>
        <div>
          <label htmlFor="f-tester">Tester</label>
          <select id="f-tester" value={testerFilter} onChange={(e) => setTesterFilter(e.target.value)}>
            <option value="">alle</option>
            {testerNamen.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <span style={{ marginLeft: 'auto', color: 'var(--espresso-70)', fontSize: '0.85rem' }}>
          {gefiltert.length} von {tests.length}
        </span>
      </div>

      {laedt ? <p className="label">Lädt …</p> : null}
      {fehler ? (
        <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>
          {fehler}
        </p>
      ) : null}
      {!laedt && gefiltert.length === 0 ? <p style={{ color: 'var(--espresso-70)' }}>Keine Verkostungen gefunden.</p> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.6rem' }}>
        {gefiltert.map((t) => {
          const eisdiele = eisdielen[t.eisdieleId];
          return (
            <div
              key={t.id}
              className="karte"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap',
                opacity: aktionId === t.id ? 0.6 : 1,
              }}
            >
              <div>
                <span className="label">{STATUS_LABEL[t.status] ?? t.status}</span>
                <div style={{ fontWeight: 600 }}>{eisdiele?.name ?? 'Unbekannte Eisdiele'}</div>
                <div style={{ color: 'var(--espresso-70)', fontSize: '0.9rem' }}>
                  {eisdiele?.ort ?? ''} · {formatDatum(t.besuchsdatum)} · von {t.testerName}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                <GuennieRating wert={t.gesamtGuennies} groesse="sm" />
                <Link className="btn btn-sekundaer" to={`/app/verwaltung/verkostungen/${t.id}`}>
                  Bearbeiten
                </Link>
                {t.status === 'VEROEFFENTLICHT' ? (
                  <button className="btn btn-sekundaer" disabled={aktionId === t.id} onClick={() => zuruecknehmen(t)}>
                    Zurückziehen
                  </button>
                ) : null}
                <button
                  className="btn btn-sekundaer"
                  disabled={aktionId === t.id}
                  onClick={() => loeschen(t)}
                  style={{ color: 'var(--erdbeere)' }}
                >
                  Löschen
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
